<template>
    <div class="middle-sidebar-content" ref="notes">
        <div class="columns middle-sidebar-top">
            <div class="column middle-sidebar-title">
                <span class="is-5 has-text-weight-bold">{{title}}</span>
            </div>

            <div class="column is-narrow middle-sidebar-actions">
                <span class="action">
                    <i class="fas fa-file"></i>
                    {{list.data.count}}
                </span>

                <note-list-tags-filter :space="space"/>

                <span @click="unread" class="action pointer">
                    <i class="fas" :class="is_unread ? 'fa-eye has-text-info' : 'fa-eye-slash'"></i>
                </span>

                <note-list-sort @select="sort"/>
            </div>
        </div>

        <note-list-tags-list :space="space"/>

        <base-button
            v-if="!is_searching && !is_bookmarked && !is_trash && space_permission('NOTE_CREATE')"
            @click="create_note"
            :loading="create.loading"
            class="is-block themed-button"
            icon="fas fa-plus"
            text="Create Note"/>

        <base-button
            v-if="is_trash && space_permission('EMPTY_TRASH')"
            @click="show_confirm = true"
            :loading="create.loading"
            class="is-block themed-button"
            icon="fas fa-trash" text="Empty Trash"/>

        <note-item
            v-for="note in list.data.results"
            :key="note.id"
            :space="space"
            :note="note"
            :is_trash="is_trash"
            :draggable="!is_trash"/>

        <mugen-scroll
            :should-handle="!list.loading && !last_page"
            :handler="load"
            scroll-container="notes"
            ref="scroll">

            <p v-if="!last_page">loading...</p>
        </mugen-scroll>

        <base-confirm-modal
            v-if="show_confirm"
            text="Do you really want to remove all notes from trash?"
            @close="show_confirm = false"
            @ok="empty_trash"/>
    </div>
</template>

<script>
import MugenScroll from 'vue-mugen-scroll';
import head from 'lodash/head';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { mapGetters, mapState } from 'vuex';
import inViewport from 'element-in-view';
import NoteItem from './NoteItem.vue';
import { copy } from '../../utils/helpers';
import NoteListTagsFilter from './NoteListTagsFilter.vue';
import get_query_list from '../../utils/router';
import NoteListTagsList from './NoteListTagsList.vue';
import NoteListSort from './NoteListSort.vue';
import { RELOAD_NOTES_LIST, Event } from '../../utils/events';


export default {
    name: 'NoteList',
    components: {
        NoteListSort,
        NoteListTagsList,
        NoteListTagsFilter,
        NoteItem,
        MugenScroll,
    },
    props: ['space'],
    data() {
        return {
            page: 1,
            last_page: false,
            first_load: true,
            show_confirm: false,
        };
    },
    computed: {
        ...mapState('notes', ['list', 'create']),
        ...mapGetters('topics', ['general_topic_id']),
        is_bookmarked() {
            return 'bookmarked' in this.$route.query;
        },
        is_unread() {
            return 'unread' in this.$route.query;
        },
        is_trash() {
            return 'trash' in this.$route.query;
        },
        is_searching() {
            return 'search' in this.$route.query;
        },
        is_saved_search() {
            return 'saved_search' in this.$route.query;
        },
        title() {
            if (this.is_bookmarked) return 'Starred';
            if (this.is_trash) return 'Trash';
            if (this.is_searching) return `"${this.$route.query.search}"`;
            if (this.is_saved_search) return `"${this.$route.query.saved_search}"`;

            return 'All Notes';
        },
    },
    destroyed() {
        this.$store.commit('notes/clear_notes_list');
    },
    mounted() {
        Event.$on(RELOAD_NOTES_LIST, this.reload);
    },
    watch: {
        '$route.query': function (new_query, old_query) {
            if (new_query.bookmarked === old_query.bookmarked
                && new_query.unread === old_query.unread
                && new_query.search === old_query.search
                && new_query.saved_search === old_query.saved_search
                && new_query.trash === old_query.trash) return;

            this.$store.commit('tags/clean_filter_tags');
            this.reload();
        },
        '$route.query.tags': function (new_value, old_value) {
            const new_tags = sortBy(get_query_list(new_value).map(Number));
            const old_tags = sortBy(get_query_list(old_value).map(Number));

            if (!isEqual(new_tags, old_tags)) {
                this.reload();
            }
        },
        '$route.query.sort': 'reload',
    },
    methods: {
        reload() {
            this.page = 1;
            this.first_load = true;
            this.load();
        },
        async load() {
            await this.$store.dispatch('notes/list', {
                ordering: this.$route.query.sort || '-created_at',
                or_tags: this.$route.query.or_tags,
                tags: get_query_list(this.$route.query.tags),
                authors: get_query_list(this.$route.query.authors),
                topics: get_query_list(this.$route.query.topics),
                search: this.$route.query.search,
                name_contains: this.$route.query.name_contains,
                content_contains: this.$route.query.content_contains,
                is_bookmarked: this.is_bookmarked,
                is_unread: this.is_unread,
                is_trash: this.is_trash,
                space_id: this.space.id,
                extend: this.page > 1,
                page: this.page,
            });

            this.page += 1;
            this.last_page = !this.list.data.next;
            this.select_first();

            // If marker (rel="scroll") in users display, load second page
            const { scroll } = this.$refs;
            if (scroll && inViewport(scroll.$el, { threshold: 0 }) && !this.last_page) {
                this.load();
            }
        },
        async create_note() {
            const note = await this.$store.dispatch('notes/create', {
                space_id: this.space.id,
                data: { topic: this.general_topic_id },
            });
            this.$store.commit('notes/add_note', note);
            const query = { ...this.$route.query, note: note.id, created: true };
            this.$router.replace({ name: 'content', params: this.$route.params, query });
        },
        select_first() {
            if (!this.first_load) return;
            if (this.$route.query.note) return;

            const first = head(this.list.data.results);

            if (first) {
                const query = copy(this.$route.query);
                query.note = first.id;
                this.$router.replace({ name: 'content', params: this.$route.params, query });
            }
            this.first_load = false;
        },
        async empty_trash() {
            this.show_confirm = false;
            await this.$store.dispatch('notes/empty_trash', { space_id: this.space.id });
            this.$store.commit('notes/clear_notes_list');

            this.$router.push({
                name: 'content',
                params: this.$route.params,
                query: { trash: null },
            });
        },
        sort(sort) {
            this.$router.push({
                name: 'content',
                params: this.$route.params,
                query: { ...this.$route.query, sort },
            });
        },
        unread() {
            const query = copy(this.$route.query);
            if ('unread' in query) {
                delete query.unread;
            } else {
                query.unread = null;
            }
            this.$router.push({ name: 'content', params: this.$route.params, query });
        },
    },
};
</script>
